/**
 * Love Knots - Shop By Interactive Controller
 * Handles URL parameter parsing, tab switching, sub-category filtering,
 * contextual promo banner toggling, and smooth scroll.
 */

(function () {
  'use strict';

  function initShopBy() {
    const mainSection = document.getElementById('shop-by-main');
    if (!mainSection) return;

    const tabButtons = mainSection.querySelectorAll('[data-shop-by-tab]');
    const tabPanels = mainSection.querySelectorAll('[data-tab-panel]');
    const subtitleEl = mainSection.querySelector('[data-tab-subtitle]');
    const promoBanners = mainSection.querySelectorAll('[data-tab-banner]');

    const subtitleMap = {
      'occasion': mainSection.dataset.subtitleOccasion || 'Where are you wearing it?',
      'craft': mainSection.dataset.subtitleCraft || 'How is it made?',
      'fit': mainSection.dataset.subtitleFit || 'Will it fit me?',
      'for_two': mainSection.dataset.subtitleFortwo || 'Who is it for?'
    };

    function normalizeTab(tabKey) {
      if (!tabKey) return 'occasion';
      tabKey = tabKey.toLowerCase().trim();
      if (tabKey === 'for-two' || tabKey === 'fortwo') return 'for_two';
      if (['occasion', 'craft', 'fit', 'for_two'].includes(tabKey)) return tabKey;
      return 'occasion';
    }

    let checkPromoReveals = null;

    function setActiveTab(tabKey, updateUrl = true, shouldScroll = false) {
      const normalized = normalizeTab(tabKey);

      // 1. Update Tab Buttons
      tabButtons.forEach(btn => {
        const btnTab = normalizeTab(btn.dataset.shopByTab);
        if (btnTab === normalized) {
          btn.classList.add('is-active');
          btn.setAttribute('aria-selected', 'true');
        } else {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-selected', 'false');
        }
      });

      // 2. Update Tab Panels
      tabPanels.forEach(panel => {
        const panelTab = normalizeTab(panel.dataset.tabPanel);
        if (panelTab === normalized) {
          panel.style.display = 'block';
          panel.classList.add('is-active');
        } else {
          panel.style.display = 'none';
          panel.classList.remove('is-active');
        }
      });

      // 3. Update Subtitle
      if (subtitleEl && subtitleMap[normalized]) {
        subtitleEl.textContent = subtitleMap[normalized];
      }

      // 4. Update Contextual Promotional Banners
      // Active tab's banner is HIDDEN; all other 3 banners are VISIBLE.
      promoBanners.forEach(banner => {
        const bannerTab = normalizeTab(banner.dataset.tabBanner);
        if (bannerTab === normalized) {
          banner.classList.add('is-hidden');
        } else {
          banner.classList.remove('is-hidden');
        }
      });

      if (typeof checkPromoReveals === 'function') {
        checkPromoReveals();
      }

      // 5. Update URL query params without reload
      if (updateUrl) {
        const urlParam = normalized === 'for_two' ? 'for-two' : normalized;
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('tab', urlParam);
        window.history.pushState({ tab: normalized }, '', currentUrl.toString());
      }

      // 6. If Fit tab is active, ensure sizes are applied
      if (normalized === 'fit') {
        const fitPanel = mainSection.querySelector('[data-tab-panel="fit"]');
        if (fitPanel) {
          const activeBtns = fitPanel.querySelectorAll('[data-fit-size-btn].is-active');
          if (activeBtns.length === 0) {
            applyFitSizeFilter(['2xs'], false);
          }
        }
      }

      // 7. Smooth Scroll if requested
      if (shouldScroll) {
        const targetOffset = mainSection.getBoundingClientRect().top + window.pageYOffset - 40;
        window.scrollTo({
          top: targetOffset,
          behavior: 'smooth'
        });
      }
    }

    // Bind Tab Header Buttons
    tabButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const tabKey = btn.dataset.shopByTab;
        setActiveTab(tabKey, true, false);
      });
    });

    // Sub-category Tile Click Handling (for Occasion, Craft, For Two)
    tabPanels.forEach(panel => {
      const subTiles = panel.querySelectorAll('[data-sub-tile]');
      const productPanels = panel.querySelectorAll('[data-product-panel]');

      subTiles.forEach(tile => {
        tile.addEventListener('click', () => {
          const targetIndex = tile.dataset.subTile;
          if (!targetIndex) return;

          subTiles.forEach(t => t.classList.toggle('is-active', t.dataset.subTile === targetIndex));
          productPanels.forEach(p => p.classList.toggle('is-active', p.dataset.productPanel === targetIndex));
        });
      });
    });

    // Fit Tab Product Pagination (10 items per batch)
    let fitPageLimit = 10;

    // Helper to filter products by multiple selected sizes with 10-per-batch Load More pagination
    function applyFitSizeFilter(sizesArray, updateUrl = true, resetPagination = true) {
      const fitPanel = mainSection.querySelector('[data-tab-panel="fit"]');
      if (!fitPanel) return;

      if (resetPagination) {
        fitPageLimit = 10;
      }

      const fitButtons = fitPanel.querySelectorAll('[data-fit-size-btn]');
      const productItems = fitPanel.querySelectorAll('.shop-by-fit-product-item');
      const emptyState = fitPanel.querySelector('[data-fit-empty]');
      const showcaseTitle = fitPanel.querySelector('[data-fit-showcase-title]');
      const resetBtn = fitPanel.querySelector('[data-fit-reset-sizes]');
      const viewAllBtn = fitPanel.querySelector('[data-fit-view-all]');
      const loadMoreWrap = fitPanel.querySelector('[data-fit-load-more-wrap]');
      const loadMoreCount = fitPanel.querySelector('[data-fit-load-more-count]');

      // Normalize array of sizes
      const activeSizes = (sizesArray || [])
        .map(s => s.toLowerCase().trim())
        .filter(Boolean);

      // Highlight matching buttons
      fitButtons.forEach(btn => {
        const btnSize = btn.dataset.fitSizeBtn.toLowerCase().trim();
        btn.classList.toggle('is-active', activeSizes.includes(btnSize));
      });

      // Filter product items
      let totalMatchedCount = 0;
      let visibleCount = 0;

      productItems.forEach(item => {
        const itemSizes = (item.dataset.fitProductSizes || '')
          .toLowerCase()
          .split(',')
          .map(s => s.trim());

        let isMatch = false;
        if (activeSizes.length === 0) {
          // If no size is active, show all
          isMatch = true;
        } else {
          isMatch = activeSizes.some(s => itemSizes.includes(s));
        }

        if (isMatch) {
          totalMatchedCount++;
          if (totalMatchedCount <= fitPageLimit) {
            item.classList.remove('is-hidden');
            visibleCount++;
          } else {
            item.classList.add('is-hidden');
          }
        } else {
          item.classList.add('is-hidden');
        }
      });

      // Update Empty State
      if (emptyState) {
        emptyState.style.display = (totalMatchedCount === 0) ? 'block' : 'none';
      }

      // Update Load More Button
      if (loadMoreWrap) {
        if (totalMatchedCount > fitPageLimit) {
          loadMoreWrap.style.display = 'flex';
        } else {
          loadMoreWrap.style.display = 'none';
        }
      }

      // Update Showcase Title
      if (showcaseTitle) {
        if (activeSizes.length === 0) {
          showcaseTitle.textContent = 'All Available Pieces';
        } else if (activeSizes.length === 1) {
          showcaseTitle.textContent = 'Picked for Size ' + activeSizes[0].toUpperCase();
        } else {
          showcaseTitle.textContent = 'Picked for Sizes: ' + activeSizes.map(s => s.toUpperCase()).join(', ');
        }
      }

      // Update Clear/Reset Button
      if (resetBtn) {
        resetBtn.style.display = (activeSizes.length > 0) ? 'inline-block' : 'none';
      }

      // Update View All URL with filters
      if (viewAllBtn) {
        if (activeSizes.length > 0) {
          const filterQueries = activeSizes.map(s => 'filter.v.option.size=' + encodeURIComponent(s.toUpperCase())).join('&');
          viewAllBtn.href = '/collections/all?' + filterQueries;
        } else {
          viewAllBtn.href = '/collections/all';
        }
      }

      // Update URL
      if (updateUrl) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('tab', 'fit');
        if (activeSizes.length > 0) {
          currentUrl.searchParams.set('size', activeSizes.join(','));
        } else {
          currentUrl.searchParams.delete('size');
        }
        window.history.pushState({ tab: 'fit', sizes: activeSizes }, '', currentUrl.toString());
      }
    }

    // Load More Button Click Handling (10 more items per click)
    const loadMoreBtn = mainSection.querySelector('[data-fit-load-more]');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', (e) => {
        e.preventDefault();
        fitPageLimit += 10;
        const fitPanel = mainSection.querySelector('[data-tab-panel="fit"]');
        if (fitPanel) {
          const currentActiveButtons = fitPanel.querySelectorAll('[data-fit-size-btn].is-active');
          const currentSizes = Array.from(currentActiveButtons).map(b => b.dataset.fitSizeBtn.toLowerCase().trim());
          applyFitSizeFilter(currentSizes, false, false);
        }
      });
    }

    // Fit Tab Main Size Selector Buttons: Single-Select Click Handling
    const fitButtons = mainSection.querySelectorAll('[data-fit-size-btn]');
    fitButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const clickedSize = btn.dataset.fitSizeBtn.toLowerCase().trim();

        const fitPanel = mainSection.querySelector('[data-tab-panel="fit"]');
        if (!fitPanel) return;

        let newSizes = [];
        if (btn.classList.contains('is-active')) {
          // If already selected, clicking it again unselects to show all pieces
          newSizes = [];
        } else {
          // Single select: activate only the clicked size
          newSizes = [clickedSize];
        }

        applyFitSizeFilter(newSizes, true, true);
      });
    });

    // Clear / Reset Sizes Click Handling
    const resetButtons = mainSection.querySelectorAll('[data-fit-reset-sizes]');
    resetButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        applyFitSizeFilter([], true, true);
      });
    });

    // Promotional Banner Size Buttons Click Handling
    const promoSizeButtons = mainSection.querySelectorAll('[data-switch-to-fit-size]');
    promoSizeButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const sizeKey = btn.dataset.switchToFitSize;
        setActiveTab('fit', false, true);
        applyFitSizeFilter([sizeKey], true, true);
      });
    });

    // Size Chart Modal Handling
    const sizeChartModal = document.getElementById('ShopBySizeChartModal');
    if (sizeChartModal) {
      const openChartTriggers = mainSection.querySelectorAll('[data-open-size-chart]');
      const closeChartTriggers = sizeChartModal.querySelectorAll('[data-close-size-chart]');

      openChartTriggers.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          sizeChartModal.style.display = 'flex';
          sizeChartModal.setAttribute('aria-hidden', 'false');
          document.body.style.overflow = 'hidden';
        });
      });

      closeChartTriggers.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          sizeChartModal.style.display = 'none';
          sizeChartModal.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
        });
      });

      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && sizeChartModal.style.display === 'flex') {
          sizeChartModal.style.display = 'none';
          sizeChartModal.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
        }
      });
    }

    // Banner CTA Buttons (e.g. "Shop by craft", "Shop for two", "Shop by fit", "Shop by occasion")
    const bannerSwitchButtons = mainSection.querySelectorAll('[data-switch-tab]');
    bannerSwitchButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const targetTab = btn.dataset.switchTab;
        setActiveTab(targetTab, true, true);
      });
    });

    // Listen to browser Back/Forward navigation
    window.addEventListener('popstate', (e) => {
      const urlParams = new URLSearchParams(window.location.search);
      const urlTab = urlParams.get('tab') || 'occasion';
      const urlSize = urlParams.get('size');
      setActiveTab(urlTab, false, false);
      if (normalizeTab(urlTab) === 'fit') {
        const sizesArr = urlSize ? urlSize.split(',') : ['2xs'];
        applyFitSizeFilter(sizesArr, false);
      }
    });

    // Initial Load from URL parameter
    const initialParams = new URLSearchParams(window.location.search);
    const initialTab = initialParams.get('tab') || 'occasion';
    const initialSize = initialParams.get('size');
    setActiveTab(initialTab, false, false);
    if (normalizeTab(initialTab) === 'fit') {
      const sizesArr = initialSize ? initialSize.split(',') : ['2xs'];
      applyFitSizeFilter(sizesArr, false);
    }

    // Promotional Cards Scroll Reveal Animation
    function initPromoCardScrollReveal() {
      const promoCards = mainSection.querySelectorAll('.shop-by-promo-card');
      if (!promoCards.length) return () => {};

      if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        promoCards.forEach(card => card.classList.add('is-revealed'));
        return () => {};
      }

      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const card = entry.target;
            if (!card.classList.contains('is-hidden')) {
              card.classList.add('is-revealed');
              revealObserver.unobserve(card);
            }
          }
        });
      }, {
        root: null,
        rootMargin: '0px 0px -40px 0px',
        threshold: 0.08
      });

      promoCards.forEach(card => {
        if (!card.classList.contains('is-hidden')) {
          revealObserver.observe(card);
        }
      });

      return function checkRevealsOnTabChange() {
        promoCards.forEach(card => {
          if (!card.classList.contains('is-hidden')) {
            if (card.classList.contains('is-revealed')) return;
            const rect = card.getBoundingClientRect();
            const inView = rect.top < window.innerHeight - 40 && rect.bottom > 0;
            if (inView) {
              requestAnimationFrame(() => {
                card.classList.add('is-revealed');
                revealObserver.unobserve(card);
              });
            } else {
              revealObserver.observe(card);
            }
          }
        });
      };
    }

    checkPromoReveals = initPromoCardScrollReveal();

    // Dynamic Real-Data Sync: Fetch /products.json to enrich variant sizes in real time
    function normalizeSizeToken(rawSize) {
      if (!rawSize) return null;
      const s = rawSize.toString().toLowerCase().trim();
      if (s === '2xs' || s === 'xxs' || s.includes('2xs') || s.includes('xxs')) return '2xs';
      if (s === 'xs' || s === 'extra small' || s === 'x-small' || s === '34') return 'xs';
      if (s === 's' || s === 'small' || s === 'size s' || s.startsWith('s /') || s.startsWith('s/') || s === '36') return 's';
      if (s === 'm' || s === 'medium' || s === 'size m' || s.startsWith('m /') || s.startsWith('m/') || s === '38') return 'm';
      if (s === 'l' || s === 'large' || s === 'size l' || s.startsWith('l /') || s.startsWith('l/') || s === '40') return 'l';
      if (s === 'xl' || s === 'extra large' || s === 'x-large' || s === 'size xl' || s.startsWith('xl /') || s.startsWith('xl/') || s === '42') return 'xl';
      if (s === '2xl' || s === 'xxl' || s === '2x-large' || s.includes('2xl') || s.includes('xxl') || s === '44') return '2xl';
      if (s === '3xl' || s === 'xxxl' || s === '3x-large' || s.includes('3xl') || s.includes('xxxl') || s === '46') return '3xl';
      return null;
    }

    async function syncRealProductSizes() {
      try {
        const endpoints = ['/collections/all/products.json?limit=250', '/products.json?limit=250'];
        let data = null;
        for (const ep of endpoints) {
          try {
            const res = await fetch(ep);
            if (res.ok) {
              data = await res.json();
              if (data && data.products && data.products.length > 0) break;
            }
          } catch (e) {}
        }

        if (!data || !data.products) return;

        const productMap = new Map();
        data.products.forEach(p => {
          const matchedSizes = new Set();
          if (p.variants) {
            p.variants.forEach(v => {
              [v.option1, v.option2, v.option3, v.title].forEach(val => {
                const token = normalizeSizeToken(val);
                if (token) matchedSizes.add(token);
              });
            });
          }
          if (matchedSizes.size > 0) {
            productMap.set(p.handle, Array.from(matchedSizes));
          }
        });

        const productItems = mainSection.querySelectorAll('.shop-by-fit-product-item');
        let updatedCount = 0;
        productItems.forEach(item => {
          const handle = item.dataset.productHandle;
          if (handle && productMap.has(handle)) {
            const realSizes = productMap.get(handle);
            const existingSizes = (item.dataset.fitProductSizes || '').split(',').filter(Boolean);
            const merged = Array.from(new Set([...existingSizes, ...realSizes]));
            item.dataset.fitProductSizes = merged.join(',');
            updatedCount++;
          }
        });

        if (updatedCount > 0) {
          const fitPanel = mainSection.querySelector('[data-tab-panel="fit"]');
          if (fitPanel) {
            const currentActiveButtons = fitPanel.querySelectorAll('[data-fit-size-btn].is-active');
            const currentSizes = Array.from(currentActiveButtons).map(b => b.dataset.fitSizeBtn.toLowerCase().trim());
            if (currentSizes.length > 0) {
              applyFitSizeFilter(currentSizes, false);
            }
          }
        }
      } catch (err) {
        console.warn('Real product size sync notice:', err);
      }
    }

    syncRealProductSizes();

    // ========================================================================
    // FOR TWO: 2-PIECE BUNDLE BUILDER CONTROLLER
    // ========================================================================
    function initForTwoBundle() {
      const bundleDock = mainSection.querySelector('[data-bundle-dock]');
      const forTwoPanel = mainSection.querySelector('[data-tab-panel="for_two"]');
      if (!bundleDock || !forTwoPanel) return;

      const slot1El = bundleDock.querySelector('[data-bundle-slot="1"]');
      const slot2El = bundleDock.querySelector('[data-bundle-slot="2"]');
      const progressWrap = bundleDock.querySelector('[data-bundle-progress-wrap]');
      const progressBar = bundleDock.querySelector('[data-bundle-progress-bar]');
      const progressText = bundleDock.querySelector('[data-bundle-progress-text]');
      const celebratePanel = bundleDock.querySelector('[data-bundle-celebrate-panel]');
      const origTotalEl = bundleDock.querySelector('[data-bundle-orig-total]');
      const finalTotalEl = bundleDock.querySelector('[data-bundle-final-total]');
      const savingsEl = bundleDock.querySelector('[data-bundle-savings]');
      const buyBundleBtn = bundleDock.querySelector('[data-bundle-buy-btn]');

      const modalEl = document.getElementById('ShopByBundleSizeModal');
      if (modalEl && modalEl.parentElement !== document.body) {
        document.body.appendChild(modalEl);
      }
      const modalBackdrop = modalEl ? modalEl.querySelector('[data-bundle-modal-close]') : null;
      const modalCloseBtn = modalEl ? modalEl.querySelector('.shop-by-bundle-modal__close-btn') : null;
      const modalSlotIndicator = modalEl ? modalEl.querySelector('[data-bundle-modal-slot-indicator]') : null;
      const modalImg = modalEl ? modalEl.querySelector('[data-bundle-modal-img]') : null;
      const modalTitle = modalEl ? modalEl.querySelector('[data-bundle-modal-title]') : null;
      const modalPrice = modalEl ? modalEl.querySelector('[data-bundle-modal-price]') : null;
      const modalSizesGrid = modalEl ? modalEl.querySelector('[data-bundle-modal-sizes-grid]') : null;

      let bundleState = {
        slot1: null,
        slot2: null
      };

      // Restore session state if available
      try {
        const saved = sessionStorage.getItem('shop_by_for_two_bundle');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed && (parsed.slot1 || parsed.slot2)) {
            bundleState = parsed;
          }
        }
      } catch (e) {}

      function saveBundleState() {
        try {
          sessionStorage.setItem('shop_by_for_two_bundle', JSON.stringify(bundleState));
        } catch (e) {}
      }

      function formatINR(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents);
        }
        const val = Math.round(Number(cents) / 100);
        return '₹' + val.toLocaleString('en-IN');
      }

      function updateSlotDisplay(slotNum, item) {
        const slotEl = slotNum === 1 ? slot1El : slot2El;
        if (!slotEl) return;

        const emptyView = slotEl.querySelector(`[data-bundle-slot-empty="${slotNum}"]`);
        const filledView = slotEl.querySelector(`[data-bundle-slot-filled="${slotNum}"]`);
        const thumbEl = slotEl.querySelector(`[data-bundle-slot-thumb="${slotNum}"]`);
        const titleEl = slotEl.querySelector(`[data-bundle-slot-title="${slotNum}"]`);
        const sizeEl = slotEl.querySelector(`[data-bundle-slot-size="${slotNum}"]`);
        const priceEl = slotEl.querySelector(`[data-bundle-slot-price="${slotNum}"]`);

        if (item) {
          slotEl.classList.remove('is-empty');
          slotEl.classList.add('is-filled');
          if (emptyView) emptyView.style.display = 'none';
          if (filledView) filledView.style.display = 'flex';
          if (thumbEl) {
            thumbEl.src = item.image;
            thumbEl.alt = item.title;
          }
          if (titleEl) titleEl.textContent = item.title;
          if (sizeEl) sizeEl.textContent = 'Size: ' + item.size;
          if (priceEl) priceEl.textContent = item.formattedPrice || formatINR(item.price);
        } else {
          slotEl.classList.remove('is-filled');
          slotEl.classList.add('is-empty');
          if (emptyView) emptyView.style.display = 'flex';
          if (filledView) filledView.style.display = 'none';
          if (thumbEl) thumbEl.src = '';
          if (titleEl) titleEl.textContent = '';
          if (sizeEl) sizeEl.textContent = '';
          if (priceEl) priceEl.textContent = '';
        }
      }

      function updateBundleUI() {
        updateSlotDisplay(1, bundleState.slot1);
        updateSlotDisplay(2, bundleState.slot2);

        const filledCount = (bundleState.slot1 ? 1 : 0) + (bundleState.slot2 ? 1 : 0);

        if (filledCount === 2) {
          // Both items filled: celebrate & show buy bundle button
          if (progressWrap) progressWrap.style.display = 'none';
          if (celebratePanel) celebratePanel.style.display = 'flex';

          const p1 = Number(bundleState.slot1.price);
          const p2 = Number(bundleState.slot2.price);
          const originalTotal = p1 + p2;
          const discountedTotal = Math.round(originalTotal * 0.8);
          const savings = originalTotal - discountedTotal;

          if (origTotalEl) origTotalEl.textContent = formatINR(originalTotal);
          if (finalTotalEl) finalTotalEl.textContent = formatINR(discountedTotal);
          if (savingsEl) savingsEl.textContent = `(Save ${formatINR(savings)} • 20% OFF)`;

        } else if (filledCount === 1) {
          // 1 item filled: show progress and prompt
          if (celebratePanel) celebratePanel.style.display = 'none';
          if (progressWrap) {
            progressWrap.style.display = 'flex';
            if (progressBar) progressBar.style.width = '50%';
            if (progressText) {
              progressText.innerHTML = '<strong>1 of 2 pieces selected!</strong> Pick 1 more piece to unlock your <strong>20% discount</strong>.';
            }
          }
        } else {
          // 0 items filled: waiting state
          if (celebratePanel) celebratePanel.style.display = 'none';
          if (progressWrap) {
            progressWrap.style.display = 'flex';
            if (progressBar) progressBar.style.width = '0%';
            if (progressText) {
              progressText.textContent = 'Select 2 products from the collection below to avail your 20% discount.';
            }
          }
        }

        // Update active badge on product cards in 'For Two'
        const allCards = forTwoPanel.querySelectorAll('[data-bundle-card-wrap]');
        allCards.forEach(card => {
          const prodId = card.dataset.productId;
          const isSlot1 = bundleState.slot1 && bundleState.slot1.productId === prodId;
          const isSlot2 = bundleState.slot2 && bundleState.slot2.productId === prodId;
          const isSelected = isSlot1 || isSlot2;

          card.classList.toggle('is-in-bundle', isSelected);
          const tooltip = card.querySelector('.shop-by-bundle-card__plus-tooltip');
          if (tooltip) {
            tooltip.textContent = isSelected ? 'In Pair ✓' : 'Add';
          }
        });
      }

      // Slot remove button handling
      bundleDock.querySelectorAll('[data-bundle-remove-btn]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const slotNum = Number(btn.dataset.bundleRemoveBtn);
          if (slotNum === 1) {
            // Move slot2 to slot1 if available
            bundleState.slot1 = bundleState.slot2 || null;
            bundleState.slot2 = null;
          } else {
            bundleState.slot2 = null;
          }
          saveBundleState();
          updateBundleUI();
        });
      });

      // Clicking empty slots in dock scrolls to products showcase
      if (slot1El) {
        slot1El.addEventListener('click', (e) => {
          if (!bundleState.slot1 && !e.target.closest('[data-bundle-remove-btn]')) {
            const showcase = forTwoPanel.querySelector('.shop-by-products-showcase');
            if (showcase) showcase.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }
      if (slot2El) {
        slot2El.addEventListener('click', (e) => {
          if (!bundleState.slot2 && !e.target.closest('[data-bundle-remove-btn]')) {
            const showcase = forTwoPanel.querySelector('.shop-by-products-showcase');
            if (showcase) showcase.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }

      // Size Selection Modal controls
      function closeModal() {
        if (!modalEl) return;
        modalEl.style.display = 'none';
        modalEl.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }

      if (modalEl) {
        modalEl.querySelectorAll('[data-bundle-modal-close]').forEach(c => {
          c.addEventListener('click', (e) => {
            e.preventDefault();
            closeModal();
          });
        });

        window.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && modalEl.style.display === 'flex') {
            closeModal();
          }
        });
      }

      // Event Delegation: Handle '+' click on any product card in forTwoPanel
      forTwoPanel.addEventListener('click', (e) => {
        const plusBtn = e.target.closest('[data-bundle-open-sizes]');
        if (!plusBtn) return;

        e.preventDefault();
        e.stopPropagation();

        const cardWrap = plusBtn.closest('[data-bundle-card-wrap]');
        if (!cardWrap || !modalEl) return;

        const productId = cardWrap.dataset.productId;
        const productTitle = cardWrap.dataset.productTitle;
        const productPrice = cardWrap.dataset.productPrice;
        const productImage = cardWrap.dataset.productImage;
        const variantsScript = cardWrap.querySelector('.shop-by-bundle-variants-json');

        let variants = [];
        if (variantsScript) {
          try {
            variants = JSON.parse(variantsScript.textContent.trim());
          } catch (err) {
            console.error('Error parsing variants JSON:', err);
          }
        }

        // Determine target slot: if slot1 is empty -> slot 1, otherwise slot 2
        let targetSlot = 1;
        if (!bundleState.slot1) {
          targetSlot = 1;
        } else if (!bundleState.slot2) {
          targetSlot = 2;
        } else {
          // Both already filled -> replace slot 2
          targetSlot = 2;
        }

        // Populate modal preview
        if (modalSlotIndicator) {
          modalSlotIndicator.textContent = targetSlot === 1
            ? 'Add as Piece 1'
            : 'Add as Piece 2 (Unlocks 20% OFF 🎉)';
        }
        if (modalImg) {
          modalImg.src = productImage;
          modalImg.alt = productTitle;
        }
        if (modalTitle) modalTitle.textContent = productTitle;
        if (modalPrice) modalPrice.textContent = formatINR(productPrice);

        // Render size chips
        if (modalSizesGrid) {
          modalSizesGrid.innerHTML = '';

          if (!Array.isArray(variants) || variants.length === 0) {
            variants = [{ id: productId, title: 'One Size', price: productPrice, available: true }];
          }

          variants.forEach(variant => {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'shop-by-bundle-modal-size-chip';
            chip.textContent = variant.title || 'Standard';

            if (variant.available === false) {
              chip.disabled = true;
              chip.title = 'Sold out';
            } else {
              chip.addEventListener('click', (evt) => {
                evt.preventDefault();
                evt.stopPropagation();

                const selectedItem = {
                  productId: productId,
                  variantId: variant.id,
                  title: productTitle,
                  size: variant.title || 'Standard',
                  price: variant.price,
                  formattedPrice: formatINR(variant.price),
                  image: productImage
                };

                if (targetSlot === 1) {
                  bundleState.slot1 = selectedItem;
                } else {
                  bundleState.slot2 = selectedItem;
                }

                saveBundleState();
                updateBundleUI();
                closeModal();

                // Scroll bundle dock into view if off-screen
                const dockRect = bundleDock.getBoundingClientRect();
                if (dockRect.top < 0 || dockRect.bottom > window.innerHeight) {
                  bundleDock.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              });
            }

            modalSizesGrid.appendChild(chip);
          });
        }

        // Show modal (anchored directly on body)
        modalEl.style.display = 'flex';
        modalEl.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';

        const dialog = modalEl.querySelector('.shop-by-bundle-modal__dialog');
        if (dialog) dialog.scrollTop = 0;
      });

      // Handle 'Buy Bundle' button click
      if (buyBundleBtn) {
        buyBundleBtn.addEventListener('click', async (e) => {
          e.preventDefault();

          if (!bundleState.slot1 || !bundleState.slot2) {
            alert('Please select two pieces to complete your bundle.');
            return;
          }

          const btnText = buyBundleBtn.querySelector('.shop-by-bundle-buy-btn__text');
          const btnSpinner = buyBundleBtn.querySelector('.shop-by-bundle-buy-btn__spinner');
          const btnArrow = buyBundleBtn.querySelector('.shop-by-bundle-buy-btn__arrow');

          buyBundleBtn.disabled = true;
          if (btnText) btnText.textContent = 'Adding Bundle...';
          if (btnSpinner) btnSpinner.style.display = 'inline-block';
          if (btnArrow) btnArrow.style.display = 'none';

          try {
            // Apply configured discount in checkout session
            const discountCode = bundleDock.dataset.discountCode || 'LOVE20';
            try {
              await fetch('/discount/' + encodeURIComponent(discountCode));
            } catch (err) {}

            // Add both items to cart via Shopify Cart API
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                items: [
                  { id: bundleState.slot1.variantId, quantity: 1 },
                  { id: bundleState.slot2.variantId, quantity: 1 }
                ],
                sections: 'cart-drawer,cart-icon-bubble'
              })
            });

            if (response.ok) {
              const data = await response.json();

              if (btnText) btnText.textContent = 'Added to Cart! ✓';
              if (btnSpinner) btnSpinner.style.display = 'none';

              // Dawn Cart Drawer Integration
              const cartDrawer = document.querySelector('cart-drawer');
              if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
                cartDrawer.renderContents(data);
              } else {
                // Fallback: update bubble & open drawer or redirect to /cart
                const cartBubble = document.querySelector('#cart-icon-bubble');
                if (cartBubble && data.sections && data.sections['cart-icon-bubble']) {
                  try {
                    const bubbleHTML = new DOMParser()
                      .parseFromString(data.sections['cart-icon-bubble'], 'text/html')
                      .querySelector('.shopify-section').innerHTML;
                    cartBubble.innerHTML = bubbleHTML;
                  } catch (e) {}
                }

                if (cartDrawer && typeof cartDrawer.open === 'function') {
                  cartDrawer.open();
                } else {
                  window.location.href = '/cart';
                  return;
                }
              }

              setTimeout(() => {
                if (btnText) btnText.textContent = 'Buy Bundle';
                if (btnArrow) btnArrow.style.display = 'inline-block';
                buyBundleBtn.disabled = false;
              }, 2200);

            } else {
              throw new Error('Failed to add bundle');
            }
          } catch (err) {
            console.error('Bundle cart error:', err);
            alert('There was a problem adding the pair to your cart. Please try again.');
            if (btnText) btnText.textContent = 'Buy Bundle';
            if (btnSpinner) btnSpinner.style.display = 'none';
            if (btnArrow) btnArrow.style.display = 'inline-block';
            buyBundleBtn.disabled = false;
          }
        });
      }

      // Initial UI sync
      updateBundleUI();
    }

    initForTwoBundle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initShopBy);
  } else {
    initShopBy();
  }
})();

